/**
 * home-today-store — hooks React Query pros 3 widgets da HomePage R8.
 *
 * Endpoints consumidos:
 *   - GET /api/v1/calendar/today    → useCalendarToday()
 *   - GET /api/v1/activities/today  → useActivitiesToday()
 *   - GET /api/v1/kpis/today        → useKpisToday()
 *
 * Refresh interval: 5 min (decisão R8 #5 do status-r8-progresso.md).
 * Combinado com cache backend (60s Calendar, 5min KPIs), evita rate-limit
 * Google sem perder responsividade.
 *
 * Timezone: detectado do browser (decisão R8 #4) via
 * `Intl.DateTimeFormat().resolvedOptions().timeZone`, fallback
 * `America/Sao_Paulo` se detecção falhar.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api, hasToken, ApiError } from './api';

// ─── Tipos ─────────────────────────────────────────────────────────────

export interface CalendarEvent {
  id: string;
  summary: string;
  description: string | null;
  start: { dateTime: string };
  end: { dateTime: string };
  location: string | null;
  hangoutLink: string | null;
  attendees: Array<{ email: string; displayName: string | null }>;
  htmlLink: string;
  isPrivate: boolean;
  status: string;
}

export interface CalendarTodayResponse {
  events: CalendarEvent[];
  lastSync: string;
  cached: boolean;
}

export interface ActivityCard {
  id: string;
  type: 'deal' | 'service_order';
  workflowId: string | null;
  workflowLabel: string;
  title: string;
  clienteNome: string;
  responsavelId: string | null;
  responsavelNome: string;
  prazoIso: string | null;
  status: string;
  url: string;
}

export interface ActivitiesTodayResponse {
  overdue: ActivityCard[];
  today: ActivityCard[];
  upcoming: ActivityCard[];
  count: { overdue: number; today: number; upcoming: number };
}

export interface KpiValue {
  value: number;
  label: string;
  hint?: string;
}

/**
 * `null` = a pessoa não tem o módulo daquele KPI, e o card não aparece
 * (07/08/2026). Antes o servidor mandava os quatro para todo mundo, e quem era
 * do Compras via as propostas do Comercial na própria home.
 */
export interface KpisTodayResponse {
  em_negociacao: KpiValue | null;
  chamados_fora_do_prazo: KpiValue | null;
  preventivas_no_mes: KpiValue | null;
  computedAt: string;
  cached: boolean;
}

// ─── Timezone resolver ─────────────────────────────────────────────────

/**
 * Detecta timezone do browser. Fallback `America/Sao_Paulo` se detecção
 * indisponível (ambientes sem `Intl`, alguns SSR setups, etc).
 *
 * Memoizado por `useTimezone` pra evitar re-render cascades.
 */
export function detectTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && typeof tz === 'string') return tz;
  } catch {
    // fall through
  }
  return 'America/Sao_Paulo';
}

export function useTimezone(): string {
  return useMemo(() => detectTimezone(), []);
}

// ─── Query keys ────────────────────────────────────────────────────────

export const HOME_TODAY_KEYS = {
  calendar: (tz: string) => ['v1', 'calendar', 'today', { tz }] as const,
  calendarRange: (fromIso: string, toIso: string) =>
    ['v1', 'calendar', 'range', { fromIso, toIso }] as const,
  activities: (tz: string) => ['v1', 'activities', 'today', { tz }] as const,
  kpis: () => ['v1', 'kpis', 'today'] as const,
};

const REFRESH_INTERVAL_MS = 5 * 60_000; // 5 min — decisão R8 #5

// ─── Hooks ────────────────────────────────────────────────────────────

/**
 * useCalendarToday — busca eventos do dia no calendário Google do user.
 *
 * Trata erro de scope insuficiente (401 + needsReauth=true) retornando
 * `scopeInsufficient: true` pra UI mostrar StatusReautorizacaoBanner.
 */
export function useCalendarToday() {
  const tz = useTimezone();
  const query = useQuery({
    queryKey: HOME_TODAY_KEYS.calendar(tz),
    queryFn: () =>
      api.get<CalendarTodayResponse>(
        `/api/v1/calendar/today?timezone=${encodeURIComponent(tz)}`
      ),
    enabled: hasToken(),
    refetchInterval: REFRESH_INTERVAL_MS,
    staleTime: REFRESH_INTERVAL_MS,
    retry: (failureCount, err) => {
      // Não retenta em 403 (scope insuficient) nem 501 (não configurado).
      // Backend usa 403 (NÃO 401) pra scope error — evita auto-logout do
      // interceptor de api.ts que limpa o JWT em qualquer 401.
      if (err instanceof ApiError && (err.status === 403 || err.status === 501)) return false;
      return failureCount < 2;
    },
  });

  const scopeInsufficient =
    query.error instanceof ApiError &&
    query.error.status === 403 &&
    (query.error.response as { needsReauth?: boolean } | null)?.needsReauth === true;

  const notConfigured =
    query.error instanceof ApiError && query.error.status === 501;

  return {
    ...query,
    scopeInsufficient,
    notConfigured,
    tz,
  };
}

/**
 * useActivitiesToday — busca atividades (deals + OSs) do user nos 3 buckets.
 */
export function useActivitiesToday() {
  const tz = useTimezone();
  return useQuery({
    queryKey: HOME_TODAY_KEYS.activities(tz),
    queryFn: () =>
      api.get<ActivitiesTodayResponse>(
        `/api/v1/activities/today?timezone=${encodeURIComponent(tz)}`
      ),
    enabled: hasToken(),
    refetchInterval: REFRESH_INTERVAL_MS,
    staleTime: REFRESH_INTERVAL_MS,
  });
}

/**
 * useCalendarRange — busca eventos do user no range arbitrário (U.3 / FEAT-V2-014).
 *
 * Backend tem cache de 5min por (user, from, to) — frontend deixa React
 * Query gerenciar TTL adicional. Pra trocar viewMode (hoje→semana→mês),
 * UI re-chama com novos `fromIso/toIso`.
 *
 * Mesmo tratamento de scope/notConfigured do `useCalendarToday`.
 */
export function useCalendarRange(fromIso: string | null, toIso: string | null) {
  const query = useQuery({
    queryKey: HOME_TODAY_KEYS.calendarRange(fromIso ?? '', toIso ?? ''),
    queryFn: () =>
      api.get<CalendarTodayResponse>(
        `/api/v1/calendar/range?from=${encodeURIComponent(fromIso!)}&to=${encodeURIComponent(toIso!)}`
      ),
    enabled: hasToken() && !!fromIso && !!toIso,
    refetchInterval: REFRESH_INTERVAL_MS,
    staleTime: REFRESH_INTERVAL_MS,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 403 || err.status === 501)) return false;
      return failureCount < 2;
    },
  });

  const scopeInsufficient =
    query.error instanceof ApiError &&
    query.error.status === 403 &&
    (query.error.response as { needsReauth?: boolean } | null)?.needsReauth === true;
  const notConfigured = query.error instanceof ApiError && query.error.status === 501;

  return { ...query, scopeInsufficient, notConfigured };
}

/**
 * useKpisToday — busca os 4 KPIs do sistema. Cache backend 5min,
 * frontend re-fetch a cada 5min.
 */
export function useKpisToday() {
  return useQuery({
    queryKey: HOME_TODAY_KEYS.kpis(),
    queryFn: () => api.get<KpisTodayResponse>('/api/v1/kpis/today'),
    enabled: hasToken(),
    refetchInterval: REFRESH_INTERVAL_MS,
    staleTime: REFRESH_INTERVAL_MS,
  });
}

// ─── CARD-VENDAS-VENDEDOR (21/08/2026) ─────────────────────────────────

/**
 * Resposta de GET /api/v1/kpis/minhas-vendas — o total que O PRÓPRIO usuário
 * vendeu no mês vigente. `vinculado: false` = a pessoa não tem vendedor do
 * Omie vinculado, e o card NÃO aparece (ausência ≠ zero). A rota não aceita
 * pedir o número de outra pessoa (403 no servidor).
 */
export interface MinhasVendasResponse {
  vinculado: boolean;
  anoMes?: string;
  valorCentavos?: number;
  /** Qual lista de categorias o card usou ('tecnica' = sem plantão nem faturamento direto de manutenção). */
  regra?: 'padrao' | 'tecnica';
  computedAt?: string;
}

export function useMinhasVendas() {
  return useQuery({
    queryKey: ['home', 'minhas-vendas'],
    queryFn: () => api.get<MinhasVendasResponse>('/api/v1/kpis/minhas-vendas'),
    enabled: hasToken(),
    refetchInterval: REFRESH_INTERVAL_MS,
    staleTime: REFRESH_INTERVAL_MS,
  });
}
